import { decodeEventLog, getAddress, getCreate2Address, getContract, parseEventLogs, toHex, } from "viem";
import { protocolErrorAbi } from "./errors.js";
import { CURRENT_CONTRACT_VERSION } from "./protocolRules.js";
import { LiquidityMode, PaymentKind, ProjectState, nativeAmount, } from "./types.js";
const addressOutput = [{ name: "", type: "address" }];
const uintOutput = [{ name: "", type: "uint256" }];
export const MAX_BUY_QUANTITY = 50n;
export const MAX_NAND_PRICE_WEI = ((1n << 256n) - 1n) / MAX_BUY_QUANTITY;
export const PROJECT_CORE_VANITY_SUFFIX = "000";
export const PROJECT_TOKEN_VANITY_SUFFIX = "1010";
export const MAX_VANITY_SEARCH_ATTEMPTS = 1000000n;
const projectParamsComponents = [
    { name: "tokenName", type: "string" },
    { name: "tokenSymbol", type: "string" },
    { name: "nandPriceWei", type: "uint256" },
    { name: "nandCap", type: "uint256" },
    { name: "liquidityMode", type: "uint8" },
];
const projectSaltsComponents = [
    { name: "projectCoreSalt", type: "bytes32" },
    { name: "projectTokenSalt", type: "bytes32" },
];
export const protocolFactoryAbi = [
    ...protocolErrorAbi,
    {
        type: "function",
        name: "PROJECT_TOKEN_ADDRESS_SUFFIX",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint16" }],
    },
    {
        type: "function",
        name: "lastUnpausedAt",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint64" }],
    },
    {
        type: "function",
        name: "pendingOwner",
        stateMutability: "view",
        inputs: [],
        outputs: addressOutput,
    },
    {
        type: "function",
        name: "acceptOwnership",
        stateMutability: "nonpayable",
        inputs: [],
        outputs: [],
    },
    {
        type: "function",
        name: "createProject",
        stateMutability: "nonpayable",
        inputs: [
            {
                name: "params",
                type: "tuple",
                components: projectParamsComponents,
            },
            { name: "salts", type: "tuple", components: projectSaltsComponents },
        ],
        outputs: [
            { name: "projectCore", type: "address" },
            { name: "projectToken", type: "address" },
            { name: "nandNFT", type: "address" },
            { name: "minerNFT", type: "address" },
        ],
    },
    {
        type: "function",
        name: "projectCount",
        stateMutability: "view",
        inputs: [],
        outputs: uintOutput,
    },
    {
        type: "function",
        name: "createProjectWithPayment",
        stateMutability: "nonpayable",
        inputs: [
            {
                name: "params",
                type: "tuple",
                components: projectParamsComponents,
            },
            { name: "paymentKind", type: "uint8" },
            { name: "paymentToken", type: "address" },
            { name: "salts", type: "tuple", components: projectSaltsComponents },
        ],
        outputs: [
            { name: "projectCore", type: "address" },
            { name: "projectToken", type: "address" },
            { name: "nandNFT", type: "address" },
            { name: "minerNFT", type: "address" },
        ],
    },
    {
        type: "function",
        name: "projectCoreInitCodeHash",
        stateMutability: "view",
        inputs: [
            { name: "params", type: "tuple", components: projectParamsComponents },
            { name: "creator", type: "address" },
        ],
        outputs: [{ name: "", type: "bytes32" }],
    },
    {
        type: "function",
        name: "projectTokenInitCodeHash",
        stateMutability: "pure",
        inputs: [
            { name: "projectCore", type: "address" },
            { name: "tokenName", type: "string" },
            { name: "tokenSymbol", type: "string" },
        ],
        outputs: [{ name: "", type: "bytes32" }],
    },
    {
        type: "function",
        name: "predictProjectAddress",
        stateMutability: "view",
        inputs: [
            { name: "salt", type: "bytes32" },
            { name: "initCodeHash", type: "bytes32" },
        ],
        outputs: [{ name: "", type: "address" }],
    },
    {
        type: "function",
        name: "paymentTokenAllowed",
        stateMutability: "view",
        inputs: [{ name: "token", type: "address" }],
        outputs: [{ name: "", type: "bool" }],
    },
    {
        type: "function",
        name: "creatorManagedLiquidityAllowed",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "bool" }],
    },
    {
        type: "function",
        name: "projectAt",
        stateMutability: "view",
        inputs: [{ name: "index", type: "uint256" }],
        outputs: addressOutput,
    },
    {
        type: "function",
        name: "isProject",
        stateMutability: "view",
        inputs: [{ name: "project", type: "address" }],
        outputs: [{ name: "", type: "bool" }],
    },
    {
        type: "function",
        name: "projectAssets",
        stateMutability: "view",
        inputs: [{ name: "project", type: "address" }],
        outputs: [
            { name: "projectToken", type: "address" },
            { name: "nandNFT", type: "address" },
            { name: "minerNFT", type: "address" },
        ],
    },
    ...["safe", "treasury", "pendingTreasury"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [],
        outputs: addressOutput,
    })),
    {
        type: "function",
        name: "globalPaused",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "bool" }],
    },
    ...["pause", "unpause", "acceptTreasury"].map((name) => ({
        type: "function",
        name,
        stateMutability: "nonpayable",
        inputs: [],
        outputs: [],
    })),
    {
        type: "function",
        name: "proposeTreasury",
        stateMutability: "nonpayable",
        inputs: [{ name: "newTreasury", type: "address" }],
        outputs: [],
    },
    {
        type: "function",
        name: "setCreatorManagedLiquidityAllowed",
        stateMutability: "nonpayable",
        inputs: [{ name: "allowed", type: "bool" }],
        outputs: [],
    },
    {
        type: "event",
        name: "CreatorManagedLiquidityAllowed",
        inputs: [
            { name: "allowed", type: "bool", indexed: false },
            { name: "executor", type: "address", indexed: true },
        ],
    },
];
export const projectCoreAbi = [
    ...protocolErrorAbi,
    ...["factory", "creator", "projectToken", "nandNFT", "minerNFT", "pair"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [],
        outputs: addressOutput,
    })),
    {
        type: "function",
        name: "paymentToken",
        stateMutability: "view",
        inputs: [],
        outputs: addressOutput,
    },
    ...[
        "state",
        "nandPriceWei",
        "nandCap",
        "sold",
        "totalRaised",
        "minimumSoldForGraduation",
        "fundraisingStartTime",
        "fundraisingEndTime",
        "graduatedAt",
        "emissionStartTime",
        "emissionEndTime",
        "lpConfirmedAt",
        "liquidityMode",
        "refundLiability",
    ].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [],
        outputs: uintOutput,
    })),
    {
        type: "function",
        name: "paymentKind",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint8" }],
    },
    {
        type: "function",
        name: "paymentConfigured",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "bool" }],
    },
    {
        type: "function",
        name: "creatorProceeds",
        stateMutability: "view",
        inputs: [{ name: "beneficiary", type: "address" }],
        outputs: uintOutput,
    },
    {
        type: "function",
        name: "treasuryCredit",
        stateMutability: "view",
        inputs: [{ name: "beneficiary", type: "address" }],
        outputs: uintOutput,
    },
    {
        type: "function",
        name: "withdrawTreasuryCredit",
        stateMutability: "nonpayable",
        inputs: [{ name: "receiver", type: "address" }],
        outputs: [
            { name: "paymentAssetAmount", type: "uint256" },
            { name: "nativeSupplementAmount", type: "uint256" },
        ],
    },
    {
        type: "function",
        name: "buyNand",
        stateMutability: "payable",
        inputs: [
            { name: "quantity", type: "uint256" },
            { name: "receiver", type: "address" },
        ],
        outputs: [
            { name: "firstTokenId", type: "uint256" },
            { name: "lastTokenId", type: "uint256" },
        ],
    },
    {
        type: "function",
        name: "finalizeFundraising",
        stateMutability: "nonpayable",
        inputs: [],
        outputs: [],
    },
];
export const paymentTokenAbi = [
    ...["name", "symbol"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "string" }],
    })),
    {
        type: "function",
        name: "decimals",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint8" }],
    },
    ...["balanceOf", "allowance"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: name === "balanceOf"
            ? [{ name: "account", type: "address" }]
            : [
                { name: "owner", type: "address" },
                { name: "spender", type: "address" },
            ],
        outputs: uintOutput,
    })),
    {
        type: "function",
        name: "approve",
        stateMutability: "nonpayable",
        inputs: [
            { name: "spender", type: "address" },
            { name: "amount", type: "uint256" },
        ],
        outputs: [{ name: "", type: "bool" }],
    },
];
export const projectTokenAbi = [
    ...protocolErrorAbi,
    ...["name", "symbol"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "string" }],
    })),
    ...["totalSupply", "cap"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [],
        outputs: uintOutput,
    })),
    {
        type: "function",
        name: "minted",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "bool" }],
    },
    {
        type: "function",
        name: "projectCore",
        stateMutability: "view",
        inputs: [],
        outputs: addressOutput,
    },
    {
        type: "function",
        name: "balanceOf",
        stateMutability: "view",
        inputs: [{ name: "account", type: "address" }],
        outputs: uintOutput,
    },
];
export const nandNftAbi = [
    ...protocolErrorAbi,
    {
        type: "function",
        name: "ownerOf",
        stateMutability: "view",
        inputs: [{ name: "tokenId", type: "uint256" }],
        outputs: addressOutput,
    },
    {
        type: "function",
        name: "purchasedAt",
        stateMutability: "view",
        inputs: [{ name: "tokenId", type: "uint256" }],
        outputs: [{ name: "", type: "uint64" }],
    },
    {
        type: "function",
        name: "transferFrom",
        stateMutability: "nonpayable",
        inputs: [
            { name: "from", type: "address" },
            { name: "to", type: "address" },
            { name: "tokenId", type: "uint256" },
        ],
        outputs: [],
    },
];
export const projectEventAbi = [
    {
        type: "event",
        name: "ProjectPaymentAssetConfigured",
        inputs: [
            { name: "projectCore", type: "address", indexed: true },
            { name: "paymentKind", type: "uint8", indexed: true },
            { name: "paymentToken", type: "address", indexed: true },
        ],
    },
    {
        type: "event",
        name: "ProjectCreated",
        inputs: [
            { name: "projectIndex", type: "uint256", indexed: true },
            { name: "creator", type: "address", indexed: true },
            { name: "projectCore", type: "address", indexed: true },
            { name: "projectToken", type: "address", indexed: false },
            { name: "nandNFT", type: "address", indexed: false },
            { name: "minerNFT", type: "address", indexed: false },
            { name: "tokenName", type: "string", indexed: false },
            { name: "tokenSymbol", type: "string", indexed: false },
            { name: "nandPriceWei", type: "uint256", indexed: false },
            { name: "nandCap", type: "uint256", indexed: false },
            { name: "liquidityMode", type: "uint8", indexed: false },
            { name: "fundraisingStartTime", type: "uint64", indexed: false },
            { name: "fundraisingEndTime", type: "uint64", indexed: false },
        ],
    },
    {
        type: "event",
        name: "ProjectAssetsBound",
        inputs: [
            { name: "projectCore", type: "address", indexed: true },
            { name: "projectToken", type: "address", indexed: true },
            { name: "nandNFT", type: "address", indexed: true },
            { name: "minerNFT", type: "address", indexed: false },
        ],
    },
    {
        type: "event",
        name: "NandPurchased",
        inputs: [
            { name: "buyer", type: "address", indexed: true },
            { name: "receiver", type: "address", indexed: true },
            { name: "firstTokenId", type: "uint256", indexed: true },
            { name: "lastTokenId", type: "uint256", indexed: false },
            { name: "quantity", type: "uint256", indexed: false },
            { name: "unitPriceWei", type: "uint256", indexed: false },
            { name: "totalPaidWei", type: "uint256", indexed: false },
            { name: "soldAfter", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "NandPaymentCollected",
        inputs: [
            { name: "buyer", type: "address", indexed: true },
            { name: "paymentToken", type: "address", indexed: true },
            { name: "amount", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "FundraisingFinalized",
        inputs: [
            { name: "outcome", type: "uint8", indexed: true },
            { name: "sold", type: "uint256", indexed: false },
            { name: "totalRaised", type: "uint256", indexed: false },
            { name: "minimumSold", type: "uint256", indexed: false },
            { name: "treasuryFee", type: "uint256", indexed: false },
            { name: "netRaised", type: "uint256", indexed: false },
            { name: "finalizedAt", type: "uint64", indexed: false },
        ],
    },
    {
        type: "event",
        name: "ProjectStateChanged",
        inputs: [
            { name: "previousState", type: "uint8", indexed: true },
            { name: "newState", type: "uint8", indexed: true },
            { name: "changedAt", type: "uint64", indexed: false },
        ],
    },
    {
        type: "event",
        name: "TokenInitialSupplyMinted",
        inputs: [
            { name: "token", type: "address", indexed: true },
            { name: "miningReceiver", type: "address", indexed: true },
            { name: "liquidityReceiver", type: "address", indexed: true },
            { name: "miningAmount", type: "uint256", indexed: false },
            { name: "liquidityAmount", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "TreasuryCredited",
        inputs: [
            { name: "treasury", type: "address", indexed: true },
            { name: "amount", type: "uint256", indexed: false },
            { name: "totalCredit", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "TreasuryCreditWithdrawn",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "treasury", type: "address", indexed: true },
            { name: "receiver", type: "address", indexed: true },
            { name: "amountWei", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "CreatorProceedsCredited",
        inputs: [
            { name: "creator", type: "address", indexed: true },
            { name: "amount", type: "uint256", indexed: false },
            { name: "totalCredit", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "PaymentTreasuryCredited",
        inputs: [
            { name: "treasury", type: "address", indexed: true },
            { name: "paymentToken", type: "address", indexed: true },
            { name: "amount", type: "uint256", indexed: false },
            { name: "totalCredit", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "PaymentTreasuryCreditWithdrawn",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "treasury", type: "address", indexed: true },
            { name: "paymentToken", type: "address", indexed: true },
            { name: "receiver", type: "address", indexed: false },
            { name: "amount", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "PaymentCreatorProceedsCredited",
        inputs: [
            { name: "creator", type: "address", indexed: true },
            { name: "paymentToken", type: "address", indexed: true },
            { name: "amount", type: "uint256", indexed: false },
            { name: "totalCredit", type: "uint256", indexed: false },
        ],
    },
];
/**
 * Searches CREATE2 addresses locally. Only the init-code hash is fetched from
 * Factory, so a normal search performs no per-attempt RPC requests.
 */
export async function findVanitySalt(options) {
    const suffix = options.suffix.toLowerCase();
    if (!/^[0-9a-f]{1,40}$/.test(suffix)) {
        throw new TypeError("Vanity suffix must contain 1–40 hexadecimal characters");
    }
    const startAt = options.startAt ?? 0n;
    const maxAttempts = options.maxAttempts ?? MAX_VANITY_SEARCH_ATTEMPTS;
    const yieldEvery = options.yieldEvery ?? 2048n;
    if (startAt < 0n || maxAttempts < 1n || yieldEvery < 1n) {
        throw new RangeError("Vanity search bounds must be positive");
    }
    const maximumUint256 = (1n << 256n) - 1n;
    for (let offset = 0n; offset < maxAttempts; offset += 1n) {
        if (options.signal?.aborted === true) {
            const error = new Error("Vanity address search was cancelled");
            error.name = "AbortError";
            throw error;
        }
        const nonce = startAt + offset;
        if (nonce > maximumUint256)
            throw new RangeError("Vanity salt exceeds bytes32");
        const salt = toHex(nonce, { size: 32 });
        const address = getCreate2Address({
            from: options.factory,
            salt,
            bytecodeHash: options.initCodeHash,
        });
        if (address.toLowerCase().endsWith(suffix)) {
            return { address, salt, nonce, attempts: offset + 1n };
        }
        if ((offset + 1n) % yieldEvery === 0n) {
            await (options.yieldControl?.() ?? Promise.resolve());
        }
    }
    throw new Error(`No CREATE2 address ending in ${suffix} was found within ${maxAttempts} tries`);
}
export function previewNandPurchase(nandPriceWei, quantity) {
    if (nandPriceWei < 1n || nandPriceWei > MAX_NAND_PRICE_WEI) {
        throw new RangeError("NAND price exceeds the on-chain uint256 batch-safe range");
    }
    if (quantity < 1n || quantity > MAX_BUY_QUANTITY) {
        throw new RangeError("NAND quantity must be between 1 and 50");
    }
    return nativeAmount(nandPriceWei * quantity);
}
export function minimumSoldForGraduation(nandCap, version = CURRENT_CONTRACT_VERSION) {
    if (nandCap < 5n || nandCap % 5n !== 0n) {
        throw new RangeError("NAND cap must be at least 5 and divisible by 5");
    }
    const half = (nandCap + 1n) / 2n;
    return version === "legacy-v3" ? half : ((half + 4n) / 5n) * 5n;
}
export function projectCapabilities(snapshot) {
    const soldOut = snapshot.sold === snapshot.nandCap;
    return {
        canBuyNand: !snapshot.paused &&
            snapshot.state === ProjectState.FUNDRAISING &&
            snapshot.sold < snapshot.nandCap &&
            snapshot.now < snapshot.fundraisingEndTime,
        canFinalizeFundraising: snapshot.state === ProjectState.FUNDRAISING &&
            (soldOut || snapshot.now >= snapshot.fundraisingEndTime) &&
            (!snapshot.paused ||
                ((snapshot.contractVersion ?? CURRENT_CONTRACT_VERSION) !== "legacy-v3" &&
                    snapshot.sold < minimumSoldForGraduation(snapshot.nandCap, snapshot.contractVersion))),
        canTransferAssets: !snapshot.paused && snapshot.state === ProjectState.ACTIVE,
        soldOut,
        minimumSoldForGraduation: minimumSoldForGraduation(snapshot.nandCap, snapshot.contractVersion),
    };
}
export function createFactoryReadContract(publicClient, address) {
    return getContract({ address, abi: protocolFactoryAbi, client: publicClient });
}
export function createFactoryWriteContract(walletClient, address) {
    return getContract({ address, abi: protocolFactoryAbi, client: walletClient });
}
export function createProjectCoreReadContract(publicClient, address) {
    return getContract({ address, abi: projectCoreAbi, client: publicClient });
}
export function createProjectCoreWriteContract(walletClient, address) {
    return getContract({ address, abi: projectCoreAbi, client: walletClient });
}
export function parseProjectReceipt(receipt) {
    return parseEventLogs({ abi: projectEventAbi, logs: receipt.logs, strict: false });
}
export function projectCreatedFromReceipt(receipt) {
    for (const log of receipt.logs) {
        try {
            const decoded = decodeEventLog({
                abi: projectEventAbi,
                eventName: "ProjectCreated",
                data: log.data,
                topics: log.topics,
            });
            if (decoded.eventName !== "ProjectCreated")
                continue;
            return {
                projectIndex: decoded.args.projectIndex,
                creator: getAddress(decoded.args.creator.toLowerCase()),
                projectCore: getAddress(decoded.args.projectCore.toLowerCase()),
                projectToken: getAddress(decoded.args.projectToken.toLowerCase()),
                nandNFT: getAddress(decoded.args.nandNFT.toLowerCase()),
                minerNFT: getAddress(decoded.args.minerNFT.toLowerCase()),
            };
        }
        catch {
            // The receipt contains events from four contracts; only one is ProjectCreated.
        }
    }
    return undefined;
}
//# sourceMappingURL=project.js.map